import { useEffect, useState } from "react";

// Pixels this white or whiter become fully transparent.
const WHITE_THRESHOLD = 235;
// Pixels in this band get a soft, partial fade instead of a hard cutoff,
// so the edge between logo and background doesn't look like a jagged ring.
const EDGE_THRESHOLD = 200;

/**
 * Best-effort client-side background removal for an uploaded org logo —
 * these are almost always a flat image with an opaque white background
 * baked in, not a transparent PNG, and there's no way to ask every org to
 * re-upload a cleaner file. Turns near-white pixels transparent so the
 * logo blends into whatever it's placed on, not just a white box.
 *
 * This is explicitly best-effort, not guaranteed: reading pixel data off
 * an image hosted on another origin (S3/CDN) needs that origin to send
 * permissive CORS headers, or the canvas read throws (a "tainted canvas").
 * On ANY failure — CORS, a broken URL, a non-image response — this just
 * returns the original `src` untouched, so the sidebar never breaks; it
 * relies on the logo's container (.sb-brand-box--logo) staying solid white
 * as the real safety net regardless of whether this enhancement works.
 */
export function useTransparentLogo(src) {
  const [url, setUrl] = useState(src);

  useEffect(() => {
    let cancelled = false;
    setUrl(src);
    if (!src) return undefined;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
            data[i + 3] = 0;
          } else if (r >= EDGE_THRESHOLD && g >= EDGE_THRESHOLD && b >= EDGE_THRESHOLD) {
            const closeness = (r + g + b) / 3;
            const fade =
              (WHITE_THRESHOLD - closeness) / (WHITE_THRESHOLD - EDGE_THRESHOLD);
            data[i + 3] = Math.round(data[i + 3] * Math.max(0, Math.min(1, fade)));
          }
        }
        ctx.putImageData(imageData, 0, 0);

        if (!cancelled) setUrl(canvas.toDataURL("image/png"));
      } catch (e) {
        // Tainted canvas (CORS) or any other failure — keep the original.
      }
    };
    img.onerror = () => {};
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return url;
}
